import React, { useEffect, useRef, useState } from 'react';

const BRAND = {
  cyan: '#00AEEF',
  magenta: '#EC008C',
  red: '#ED1C24',
};

const BRAND_COLORS = [BRAND.cyan, BRAND.magenta, BRAND.red, BRAND.cyan, BRAND.magenta, BRAND.red];

const DocIcon = ({ color, children }) => (
  <div
    className="relative flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl transition-all duration-500 group-hover:scale-110 z-10"
    style={{
      background: `${color}12`,
      boxShadow: `0 4px 20px ${color}18`,
    }}
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {children}
    </svg>
  </div>
);

const documents = [
  {
    id: '01',
    title: 'Aadhaar Card',
    subtitle: 'आधार कार्ड',
    icon: (
      <>
        <path d="M16 10h2" />
        <path d="M16 14h2" />
        <path d="M6.17 15a3 3 0 0 1 5.66 0" />
        <circle cx="9" cy="11" r="2" />
        <rect x="2" y="5" width="20" height="14" rx="2" />
      </>
    ),
  },
  {
    id: '02',
    title: 'PAN Card (Linked)',
    subtitle: 'पॅन कार्ड - लिंक असणे आवश्यक',
    icon: (
      <>
        <rect width="20" height="14" x="2" y="5" rx="2" />
        <line x1="2" x2="22" y1="10" y2="10" />
      </>
    ),
  },
  {
    id: '03',
    title: 'ITR file with password',
    subtitle: 'ITR फाईल - पासवर्ड घेणे',
    icon: (
      <>
        <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
        <path d="M14 2v4a2 2 0 0 0 2 2h4" />
        <path d="M10 9H8" />
        <path d="M16 13H8" />
        <path d="M16 17H8" />
      </>
    ),
  },
  {
    id: '04',
    title: '1 Passport size photo',
    subtitle: '१ फोटो',
    icon: (
      <>
        <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
        <circle cx="9" cy="9" r="2" />
        <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
      </>
    ),
  },
  {
    id: '05',
    title: 'Cancelled Cheque',
    subtitle: 'कॅन्सल चेक',
    icon: (
      <>
        <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
        <path d="M14 2v4a2 2 0 0 0 2 2h4" />
        <path d="m9 15 2 2 4-4" />
      </>
    ),
  },
  {
    id: '06',
    title: 'Nominee papers',
    subtitle: 'वारसदार चे पेपर - आधार व पॅन कार्ड',
    icon: (
      <>
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <path d="M16 3.128a4 4 0 0 1 0 7.744" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
        <circle cx="9" cy="7" r="4" />
      </>
    ),
  },
];

function DocumentCard({ doc, index, isVisible, accent }) {
  return (
    <div
      className={`about-reveal about-card-shine group relative ${isVisible ? 'about-visible' : ''}`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className="relative rounded-[1.4rem] p-[2px] ses-brand-gradient h-full transition-all duration-500 group-hover:-translate-y-2 group-hover:shadow-xl group-hover:shadow-slate-200/80">
        <div className="relative flex h-full items-start gap-4 rounded-[1.3rem] bg-white p-6 overflow-hidden">
          {/* Watermark number */}
          <span
            className="absolute top-3 right-4 font-mono text-5xl font-black leading-none transition-colors duration-500"
            style={{ color: `${accent}12` }}
          >
            {doc.id}
          </span>

          {/* Accent glow */}
          <div
            className="absolute -bottom-6 -right-6 w-24 h-24 rounded-full blur-2xl opacity-0 group-hover:opacity-40 transition-opacity duration-500"
            style={{ background: accent }}
          />

          <DocIcon color={accent}>{doc.icon}</DocIcon>

          <div className="relative z-10 pt-1 min-w-0 flex-1">
            <span
              className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest mb-2"
              style={{ color: accent, background: `${accent}12` }}
            >
              Step {doc.id}
            </span>
            <h3 className="text-lg font-bold text-slate-900 leading-snug group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-[#00AEEF] group-hover:to-[#EC008C] transition-all duration-300">
              {doc.title}
            </h3>
            <p className="mt-1.5 text-sm font-medium text-slate-500">{doc.subtitle}</p>

            <div className="mt-3 h-0.5 w-0 group-hover:w-full rounded-full ses-brand-gradient transition-all duration-500" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DocumentsRequired() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="documents-section"
      className="relative overflow-hidden bg-white py-16 px-4 md:px-10 md:py-24 font-display"
    >
      {/* Light gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-white via-slate-50 to-white pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-72 bg-gradient-to-b from-[#EC008C]/8 via-[#00AEEF]/6 to-transparent pointer-events-none" />
      <div className="absolute top-1/3 -left-24 w-72 h-72 rounded-full bg-[#00AEEF]/10 blur-[90px] pointer-events-none" />
      <div className="absolute bottom-1/4 -right-24 w-80 h-80 rounded-full bg-[#ED1C24]/8 blur-[100px] pointer-events-none" />
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[500px] h-40 bg-gradient-to-t from-[#EC008C]/6 to-transparent blur-2xl pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-1 ses-brand-gradient opacity-60" />

      <div className="relative mx-auto max-w-6xl">
        {/* Header */}
        <div
          className={`about-reveal mb-12 md:mb-14 text-center ${isVisible ? 'about-visible' : ''}`}
        >
          <span className="mb-4 inline-flex items-center gap-2 rounded-full p-[1.5px] ses-brand-gradient shadow-sm">
            <span className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-600">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={BRAND.magenta} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
                <path d="M14 2v4a2 2 0 0 0 2 2h4" />
                <path d="m9 15 2 2 4-4" />
              </svg>
              Booking Checklist
            </span>
          </span>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">
            Documents{' '}
            <span className="ses-brand-text">Required</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-slate-500 leading-relaxed">
            Please prepare the following documents to secure your MHADA flat allocation.
          </p>
          <div className="mx-auto mt-6 h-1 w-24 rounded-full ses-brand-gradient opacity-80" />
        </div>

        {/* Progress indicator — desktop */}
        <div
          className={`about-reveal hidden lg:flex items-center justify-between mb-8 px-4 ${isVisible ? 'about-visible' : ''}`}
          style={{ transitionDelay: '150ms' }}
        >
          {documents.map((doc, i) => (
            <React.Fragment key={doc.id}>
              <div
                className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold text-white shadow-md"
                style={{ background: BRAND_COLORS[i] }}
              >
                {doc.id}
              </div>
              {i < documents.length - 1 && (
                <div className="flex-1 h-0.5 mx-2 ses-brand-gradient opacity-40 rounded-full" />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Documents grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {documents.map((doc, index) => (
            <DocumentCard
              key={doc.id}
              doc={doc}
              index={index}
              isVisible={isVisible}
              accent={BRAND_COLORS[index % BRAND_COLORS.length]}
            />
          ))}
        </div>

        {/* Important note */}
        <div
          className={`about-reveal mt-10 md:mt-12 mx-auto max-w-2xl rounded-3xl p-[2px] ses-brand-gradient shadow-lg ${
            isVisible ? 'about-visible' : ''
          }`}
          style={{ transitionDelay: '650ms' }}
        >
          <div className="rounded-[1.35rem] bg-white px-6 py-5 flex items-start sm:items-center gap-4">
            <div
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl"
              style={{ background: `${BRAND.magenta}14` }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={BRAND.magenta} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-bold text-slate-900">Important Reminder</p>
              <p className="text-sm text-slate-500 mt-0.5">
                Please bring Xerox copies of all documents{' '}
                <span className="font-medium text-slate-700">(Xerox घेऊन येणे)</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
